import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";

const ConsumptionHistory = ({
  consumption,
  loading,
  error,
  fetchConsumptionHistory,
  fetchConsumptionReport,
  locationId,
  isAdmin
}) => {
  // Filter state
  const [dateFrom, setDateFrom] = useState(moment().subtract(7, 'days').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'));
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPurpose, setFilterPurpose] = useState("ALL");

  // View mode: 'history' or 'report'
  const [viewMode, setViewMode] = useState("history");
  const [groupBy, setGroupBy] = useState("day");

  // Detail modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Memoized fetch function
  const fetchData = useCallback(() => {
    if (locationId) {
      if (viewMode === 'history') {
        fetchConsumptionHistory(locationId, dateFrom, dateTo);
      } else {
        fetchConsumptionReport(locationId, groupBy, dateFrom, dateTo);
      }
    }
  }, [locationId, viewMode, dateFrom, dateTo, groupBy, fetchConsumptionHistory, fetchConsumptionReport]);

  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get purpose badge styling
  const getPurposeBadgeClass = (purpose) => {
    switch (purpose?.toUpperCase()) {
      case "REGULAR_USE":
      case "DAILY_OPERATIONS":
        return "badge-success";
      case "EXPIRED":
        return "badge-danger";
      case "DAMAGED":
        return "badge-warning";
      case "TRANSFER":
        return "badge-info";
      case "ADJUSTMENT":
        return "badge-secondary";
      default:
        return "badge-light";
    }
  };

  // Format purpose text
  const formatPurpose = (purpose) => {
    if (!purpose) return 'N/A';
    return purpose.replace(/_/g, ' ').toLowerCase()
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter and sort history
  const getFilteredHistory = () => {
    if (!consumption.history || consumption.history.length === 0) return [];

    let filtered = [...consumption.history];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(record =>
        record.item_name?.toLowerCase().includes(search) ||
        String(record.inventory_id || '').includes(search) ||
        record.consumed_by_name?.toLowerCase().includes(search)
      );
    }

    if (filterPurpose !== "ALL") {
      filtered = filtered.filter(record =>
        record.purpose?.toUpperCase() === filterPurpose
      );
    }

    // Sort by date descending
    filtered.sort((a, b) =>
      new Date(b.consumption_date) - new Date(a.consumption_date)
    );

    return filtered;
  };

  // Calculate summary stats
  const getSummaryStats = () => {
    const history = consumption.history || [];
    const totalRecords = history.length;
    const totalQuantity = history.reduce((sum, r) => sum + (parseFloat(r.quantity) || 0), 0);
    const uniqueItems = new Set(history.map(r => r.item_name)).size;
    const uniqueUsers = new Set(history.map(r => r.consumed_by_name)).size;

    return { totalRecords, totalQuantity, uniqueItems, uniqueUsers };
  };

  // Handle view details
  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRecord(null);
  };

  const filteredHistory = getFilteredHistory();
  const stats = getSummaryStats();

  // Format report data for display
  const getReportData = () => {
    if (!consumption.report?.report) return [];
    return consumption.report.report;
  };

  const reportData = getReportData();

  return (
    <>
      {/* Summary Stats Row */}
      <div className="row mb-3">
        <div className="col-6 col-md-3">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0">{stats.totalRecords}</h4>
              <small className="text-muted">Total Records</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0" style={{ color: '#36a9e1' }}>{stats.totalQuantity.toFixed(1)}</h4>
              <small className="text-muted">Total Consumed</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0" style={{ color: '#bdea74' }}>{stats.uniqueItems}</h4>
              <small className="text-muted">Unique Items</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0" style={{ color: '#fabb3d' }}>{stats.uniqueUsers}</h4>
              <small className="text-muted">Users</small>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="card">
        {/* Filter Header */}
        <div className="card-header reports-filter">
          <form className="row mb-1">
            <div className="col-12 col-sm-6 col-md-2">
              <label className="col-form-label filter-label" htmlFor="dateFrom">
                <i className="fa fa-calendar mr-05 ml-05"></i>
                From Date
              </label>
              <input
                type="date"
                id="dateFrom"
                className="form-control"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-6 col-md-2">
              <label className="col-form-label filter-label ml-05" htmlFor="dateTo">
                To Date
              </label>
              <input
                type="date"
                id="dateTo"
                className="form-control"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-6 col-md-2">
              <label className="col-form-label filter-label ml-05" htmlFor="viewMode">
                View Mode
              </label>
              <select
                className="form-control"
                id="viewMode"
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
              >
                <option value="history">Transaction History</option>
                <option value="report">Analytics Report</option>
              </select>
            </div>
            {viewMode === 'report' ? (
              <div className="col-12 col-sm-6 col-md-2">
                <label className="col-form-label filter-label ml-05" htmlFor="groupBy">
                  Group By
                </label>
                <select
                  className="form-control"
                  id="groupBy"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                >
                  <option value="day">Daily</option>
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                </select>
              </div>
            ) : (
              <>
                <div className="col-12 col-sm-6 col-md-2">
                  <label className="col-form-label filter-label ml-05" htmlFor="search">
                    <i className="fa fa-search mr-05"></i>
                    Search
                  </label>
                  <input
                    type="text"
                    id="search"
                    className="form-control"
                    placeholder="Item, inventory ID, user..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-2">
                  <label className="col-form-label filter-label ml-05" htmlFor="purpose">
                    Purpose
                  </label>
                  <select
                    className="form-control"
                    id="purpose"
                    value={filterPurpose}
                    onChange={(e) => setFilterPurpose(e.target.value)}
                  >
                    <option value="ALL">All Purposes</option>
                    <option value="REGULAR_USE">Regular Use</option>
                    <option value="DAILY_OPERATIONS">Daily Operations</option>
                    <option value="EXPIRED">Expired</option>
                    <option value="DAMAGED">Damaged</option>
                    <option value="TRANSFER">Transfer</option>
                    <option value="ADJUSTMENT">Adjustment</option>
                  </select>
                </div>
              </>
            )}
            <div className="col-12 col-sm-6 col-md-2 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-secondary w-100"
                onClick={() => {
                  setDateFrom(moment().subtract(7, 'days').format('YYYY-MM-DD'));
                  setDateTo(moment().format('YYYY-MM-DD'));
                  setSearchTerm("");
                  setFilterPurpose("ALL");
                  setGroupBy("day");
                }}
              >
                <i className="fa fa-refresh mr-05"></i>
                Reset
              </button>
            </div>
          </form>
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-danger m-3" role="alert">
            {error}
          </div>
        )}

        {/* Content based on view mode */}
        {viewMode === 'history' ? (
          // Transaction History View
          <>
            {loading && filteredHistory.length === 0 ? (
              <div className="card-body text-center py-5">
                <i className="fa fa-spinner fa-spin fa-2x"></i>
                <p className="mt-2 text-muted">Loading consumption history...</p>
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="shipment-list--empty">
                <div className="fa fa-history icon" aria-hidden="true"></div>
                <div>No consumption records found</div>
              </div>
            ) : (
              <table className="table table-striped table-bordered datatable mb-0">
                <thead>
                  <tr>
                    <th>Date/Time</th>
                    <th>Item</th>
                    <th className="hidden-xs-down">Inventory ID</th>
                    <th className="text-center">Quantity</th>
                    <th className="text-center">Purpose</th>
                    <th className="hidden-xs-down">User</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map((record) => (
                    <tr key={record.id} style={record.is_untracked ? { backgroundColor: '#fff9e6' } : {}}>
                      <td>
                        <div>
                          <strong>{moment(record.consumption_date).format('MM/DD/YY')}</strong>
                          <br />
                          <small className="text-muted">
                            {moment(record.consumption_date).format('h:mm A')}
                          </small>
                        </div>
                      </td>
                      <td>
                        <strong>{record.item_name || 'Unknown Item'}</strong>
                        {record.is_untracked && (
                          <span className="badge badge-pill badge-warning ms-1" style={{ fontSize: '10px' }}>
                            <i className="fa fa-exclamation-triangle mr-05"></i>
                            Untracked
                          </span>
                        )}
                      </td>
                      <td className="hidden-xs-down">
                        {record.is_untracked || !record.inventory_id ? (
                          <span className="text-muted" style={{ fontSize: '11px' }}>N/A</span>
                        ) : (
                          <code style={{ fontSize: '11px' }}>{record.inventory_id}</code>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="badge badge-pill badge-info">{record.quantity}</span>
                      </td>
                      <td className="text-center">
                        <span className={`badge badge-pill ${getPurposeBadgeClass(record.purpose)}`}>
                          {formatPurpose(record.purpose)}
                        </span>
                      </td>
                      <td className="hidden-xs-down">
                        {record.consumed_by_name || 'Unknown'}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn-sm btn-info"
                          onClick={() => handleViewDetails(record)}
                          title="View details"
                        >
                          <i className="fa fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        ) : (
          // Analytics Report View
          <>
            {loading && reportData.length === 0 ? (
              <div className="card-body text-center py-5">
                <i className="fa fa-spinner fa-spin fa-2x"></i>
                <p className="mt-2 text-muted">Loading consumption report...</p>
              </div>
            ) : reportData.length === 0 ? (
              <div className="shipment-list--empty">
                <div className="fa fa-bar-chart icon" aria-hidden="true"></div>
                <div>No report data available</div>
              </div>
            ) : (
              <>
                {/* Report Period Info */}
                {consumption.report?.period && (
                  <div className="card-body py-2" style={{ backgroundColor: '#f9f9f9', borderBottom: '1px solid #dbdee0' }}>
                    <small className="text-muted">
                      <i className="fa fa-calendar mr-05"></i>
                      Report Period: {moment(consumption.report.period.from).format('MMM DD, YYYY')} - {moment(consumption.report.period.to).format('MMM DD, YYYY')}
                      <span className="mx-2">|</span>
                      <i className="fa fa-clock-o mr-05"></i>
                      Generated: {moment(consumption.report.generated_at).format('MMM DD, YYYY h:mm A')}
                    </small>
                  </div>
                )}

                <table className="table table-striped table-bordered datatable mb-0">
                  <thead>
                    <tr>
                      <th>
                        {groupBy === 'day' ? 'Date' : groupBy === 'week' ? 'Week' : 'Month'}
                      </th>
                      <th>Item</th>
                      <th className="text-center">Total Consumed</th>
                      <th className="text-center">Transactions</th>
                      <th className="text-center">Avg per Transaction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((row, index) => {
                      const periodKey = groupBy === 'day'
                        ? row.consumption_date__date
                        : groupBy === 'week'
                          ? `Week ${row.consumption_date__week}`
                          : `Month ${row.consumption_date__month}`;

                      const avgPerTransaction = row.consumption_count > 0
                        ? (row.total_consumed / row.consumption_count).toFixed(1)
                        : 0;

                      return (
                        <tr key={index}>
                          <td>
                            {groupBy === 'day'
                              ? moment(periodKey).format('MM/DD/YY')
                              : periodKey}
                          </td>
                          <td>
                            <strong>{row.stock__item__name || 'Unknown'}</strong>
                          </td>
                          <td className="text-center">
                            <span className="badge badge-pill badge-info">
                              {row.total_consumed?.toFixed(1) || 0}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge badge-pill badge-secondary">
                              {row.consumption_count || 0}
                            </span>
                          </td>
                          <td className="text-center">
                            {avgPerTransaction}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Report Summary Footer */}
                <div className="card-footer" style={{ backgroundColor: '#f9f9f9' }}>
                  <div className="row text-center">
                    <div className="col-4">
                      <strong>{reportData.length}</strong>
                      <br />
                      <small className="text-muted">Data Points</small>
                    </div>
                    <div className="col-4">
                      <strong>
                        {reportData.reduce((sum, r) => sum + (r.total_consumed || 0), 0).toFixed(1)}
                      </strong>
                      <br />
                      <small className="text-muted">Total Consumed</small>
                    </div>
                    <div className="col-4">
                      <strong>
                        {reportData.reduce((sum, r) => sum + (r.consumption_count || 0), 0)}
                      </strong>
                      <br />
                      <small className="text-muted">Total Transactions</small>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Refresh indicator */}
        {loading && ((viewMode === 'history' && filteredHistory.length > 0) ||
          (viewMode === 'report' && reportData.length > 0)) && (
          <div className="card-footer text-center py-2">
            <small className="text-muted">
              <i className="fa fa-refresh fa-spin mr-05"></i> Refreshing...
            </small>
          </div>
        )}
      </div>

      {/* Record Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} dialogClassName="modal-logut" animation={false}>
        <div className="card mb-0">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>
              <i className="fa fa-info-circle mr-05"></i>
              Consumption Details
            </span>
            <button type="button" className="btn-close" onClick={handleCloseModal}></button>
          </div>

          <div className="card-body p-0">
            {selectedRecord && (
              <table className="table table-striped table-sm mb-0">
                <tbody>
                  <tr>
                    <td className="ps-3"><strong>Date/Time</strong></td>
                    <td>{moment(selectedRecord.consumption_date).format('MMM DD, YYYY h:mm A')}</td>
                  </tr>
                  <tr>
                    <td className="ps-3"><strong>Item</strong></td>
                    <td>{selectedRecord.item_name || 'Unknown'}</td>
                  </tr>
                  <tr>
                    <td className="ps-3"><strong>Inventory ID</strong></td>
                    <td>
                      {selectedRecord.is_untracked || !selectedRecord.inventory_id ? (
                        <span className="text-muted">N/A</span>
                      ) : (
                        <code>{selectedRecord.inventory_id}</code>
                      )}
                    </td>
                  </tr>
                  {selectedRecord.is_untracked && (
                    <tr style={{ backgroundColor: '#fff9e6' }}>
                      <td className="ps-3"><strong>Status</strong></td>
                      <td>
                        <span className="badge badge-pill badge-warning">
                          <i className="fa fa-exclamation-triangle mr-05"></i>
                          Untracked Item
                        </span>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="ps-3"><strong>Location</strong></td>
                    <td>{selectedRecord.location_name || 'Unknown'}</td>
                  </tr>
                  <tr>
                    <td className="ps-3"><strong>Quantity</strong></td>
                    <td>
                      <span className="badge badge-pill badge-info">{selectedRecord.quantity}</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="ps-3"><strong>Purpose</strong></td>
                    <td>
                      <span className={`badge badge-pill ${getPurposeBadgeClass(selectedRecord.purpose)}`}>
                        {formatPurpose(selectedRecord.purpose)}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="ps-3"><strong>Recorded By</strong></td>
                    <td>{selectedRecord.consumed_by_name || 'Unknown'}</td>
                  </tr>
                  <tr>
                    <td className="ps-3"><strong>Notes</strong></td>
                    <td>{selectedRecord.notes || <span className="text-muted">No notes</span>}</td>
                  </tr>
                  {selectedRecord.production_session && (
                    <tr>
                      <td className="ps-3"><strong>Production Session</strong></td>
                      <td>
                        <span className="badge badge-pill badge-secondary">
                          #{selectedRecord.production_session}
                        </span>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="ps-3"><strong>Record ID</strong></td>
                    <td><small className="text-muted">#{selectedRecord.id}</small></td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          <div className="card-footer text-end">
            <button className="btn btn-secondary" onClick={handleCloseModal}>
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ConsumptionHistory;
